import React, { useMemo } from 'react';

import { twMerge } from 'tailwind-merge';

import { Form, Formik } from 'formik';

import * as Yup from 'yup';

import Icon from 'components/icon/icon';
import TextAreaField from 'form-elements/text-area-field';

import Dialog from 'components/dialog/dialog';
import Typography from 'components/typography/typography';
import Divider from 'components/divider/divider';
import Button from 'components/button/button';

import { createLocale } from 'locales';
import StarRatingField from 'form-elements/star-rating-field';
import useGetOrganizationFeedbackOnUserJob from 'state/remote/remote-organization/use-get-organization-feedback-on-user-job';

interface FeedbackDialogProps {
  isOpen?: boolean;
  onAcceptClicked?: (values: { feedback: string; rating: number }) => void;
  onBackClicked?: () => void;
  onCloseClicked?: () => void;
  job: { id: number; date: string; freelancer?: string };
}

const localize = createLocale('organizationWeekPlannerLocales');

const FeedbackDialogSchema = Yup.object().shape({
  feedback: Yup.string()
    .required()
    .min(3, localize('minMessageLengthErrorMessage', { count: 3 }))
    .max(250, localize('maxMessageLengthErrorMessage', { count: 250 })),
  rating: Yup.number()
    .required(localize('ratingCannotBeEmpty'))
    .min(0, localize('ratingCannotBeEmpty'))
    .max(5),
});

const FeedbackDialog: React.FC<FeedbackDialogProps> = (props) => {
  const { onAcceptClicked, onBackClicked } = props;
  const paddingClassName = twMerge('py-4 px-7');

  const headerClassNames = twMerge(
    'flex flex-row justify-between w-full',
    paddingClassName,
  );

  const bodyClassNames = twMerge(
    'flex flex-col items-center gap-y-6 w-full justify-center',
    paddingClassName,
  );

  const footerClassNames = twMerge(
    'flex flex-row justify-end w-full gap-x-4',
    paddingClassName,
  );

  const titleClassNames = twMerge('text-purple');

  const { data, isLoading } = useGetOrganizationFeedbackOnUserJob(props.job.id);

  const initialValue = useMemo(() => {
    return {
      feedback: data.feedback ?? '',
      rating: data.rating ?? -1,
    };
  }, [data]);

  return (
    <Dialog
      isOpen={props.isOpen}
      onClose={() => props.onCloseClicked?.()}
      className={twMerge(isLoading && 'pointer-events-none cursor-progress')}
    >
      <div id="header" className={headerClassNames}>
        <Typography variant="page-heading" as="p" className={titleClassNames}>
          {localize('feedbackDialogHeaderTitle')}
        </Typography>
        <button type="button" onClick={() => props.onCloseClicked?.()}>
          <Icon icon="close" />
        </button>
      </div>
      <Divider orientation="horizontal" className="w-full" />
      <Formik
        key={initialValue.feedback}
        initialValues={initialValue}
        validationSchema={FeedbackDialogSchema}
        onSubmit={(values) => {
          onAcceptClicked?.({
            feedback: values.feedback.trim(),
            rating: values.rating,
          });
        }}
      >
        {() => (
          <Form>
            <div id="body" className={bodyClassNames}>
              <Typography as="p" variant="page-subheading">
                {localize('feedbackDialogBodyTitle', {
                  freelancer: props.job.freelancer,
                })}
              </Typography>
              <Typography as="p" variant="body-medium">
                {localize('feedbackDialogBodyMessage', {
                  date: props.job.date,
                  freelancer: props.job.freelancer,
                })}
              </Typography>
              <StarRatingField
                iconProps={{ size: 64 }}
                name="rating"
                initialValue={data.rating}
              />
              <TextAreaField
                rows={5}
                textareaClassName="w-full resize-none"
                placeholder={localize('explanation')}
                name="feedback"
              />
            </div>
            <Divider orientation="horizontal" className="w-full" />
            <div id="footer" className={footerClassNames}>
              <Button
                variant="outlined"
                label={localize('goBack')}
                onClick={onBackClicked}
              />
              <Button
                variant="primary"
                label={localize('sendFeedback')}
                type="submit"
                disabled={isLoading || Object.keys(data).length !== 0}
                className={twMerge(
                  (isLoading || Object.keys(data).length !== 0) && 'opacity-50',
                )}
              />
            </div>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default FeedbackDialog;
